// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/eventFocus.ts
// 🔧 PURPOSE: L'evento toccato sulla Home si apre in cima alla pagina
//             Eventi, invece di lasciare l'ospite a cercarlo nella lista.
//
//             La Home porta a /events?focus=<id>. L'id è quello di sempre
//             (makeEventId: titolo + giorno), lo stesso da /api/events, dal
//             proxy e dalla cache salvata, e non cambia quando arriva l'ora
//             (enrichTimes): preferiti e promemoria usano già lui. Stando
//             nell'indirizzo regge il reload e il "indietro". Se l'evento
//             non è più nella lista, la pagina si apre come sempre.
// ============================================

import type { Event } from '../contexts/EventsContext';

/** Il parametro dell'indirizzo: /events?focus=evt_1a2b3c. */
export const EVENT_FOCUS_PARAM = 'focus';

/** Gli id sono corti e senza simboli (evt_ + base 36): altro dall'indirizzo non si cerca. */
const FOCUS_ID_RE = /^[\w-]{1,64}$/;

/** Il link della Home a un evento: la pagina Eventi con quell'evento in cima. */
export function eventFocusPath(event: Pick<Event, 'id'>): string {
  if (!event.id || !FOCUS_ID_RE.test(event.id)) return '/events';
  return `/events?${EVENT_FOCUS_PARAM}=${encodeURIComponent(event.id)}`;
}

/** L'id da mettere in cima, letto da `location.search` ("?focus=..."); null se manca o non è un id. */
export function focusIdFromSearch(search: string): string | null {
  let value: string | null;
  try {
    value = new URLSearchParams(search).get(EVENT_FOCUS_PARAM);
  } catch {
    return null;
  }
  return value && FOCUS_ID_RE.test(value) ? value : null;
}

/** L'evento da mettere in cima, se c'è ancora nella lista; altrimenti null. */
export function findFocusedEvent<T extends Pick<Event, 'id'>>(events: T[], id: string | null): T | null {
  if (!id) return null;
  for (const event of events) {
    if (event.id === id) return event;
  }
  return null;
}
